import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { ENTITY_TYPES } from '@sales-center/common'
import { getLeadNotes } from '@user/store/leadNotes/api'
import { NoteList } from './NoteList'
import {
  selectAllLeadNotes,
  selectLeadNoteSlice,
  setPage,
} from '@user/store/leadNotes/slice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useGetLeadNotesOnMount } from '@user/store/leadNotes/hooks/useGetLeadNotesOnMount'

import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { useUpdateNoteList } from '@user/components/Notes/EntityNotes/hooks/useUpdateNoteList'
import { NoteDetailsProps } from '@user/components/Leads/LeadCard/NotesDrawer/typings'

interface Props {
  leadId: number
  canUpdate: boolean
  showPestPacNote: boolean
  showKnockStatusNote?: boolean
  backToNotes?: boolean
  externalLinkId: string
  sortBy: string
  detail: NoteDetailsProps
  setBackToNotes?: (value: boolean, id: number) => void
  locationsStatus: 'pending' | 'success' | 'fail'
}

export function LeadNotes(props: Props) {
  const {
    leadId,
    canUpdate,
    showPestPacNote,
    showKnockStatusNote,
    externalLinkId,
    sortBy,
    detail,
    backToNotes,
    setBackToNotes,
    locationsStatus,
  } = props
  const notesPerPage = 3
  const dispatch = useAppDispatch()

  const status = useGetLeadNotesOnMount({ leadId, take: notesPerPage })

  const notes = useSelector(selectAllLeadNotes)
  const { count, listIsStale, page, take } = useSelector(selectLeadNoteSlice)
  const { onUpdateNoteLIst } = useUpdateNoteList(leadId, ENTITY_TYPES.Lead)

  const getLeadNotesByPage = useCallback(
    async (pageNumber) => {
      dispatch(
        getLeadNotes({
          leadId,
          query: { take: notesPerPage, skip: (pageNumber - 1) * notesPerPage },
        }),
      )
      dispatch(setPage(pageNumber))
    },
    [dispatch, leadId],
  )

  useEffect(() => {
    if (listIsStale) {
      dispatch(
        getLeadNotes({
          leadId,
          query: { take: notesPerPage },
        }),
      )
      dispatch(setPage(1))
    }
  }, [dispatch, leadId, listIsStale])

  return (
    <Stack alignItems={status == 'pending' ? 'center' : 'initial'}>
      {(status == 'pending' || locationsStatus == 'pending') && (
        <CircularProgress />
      )}

      {status == 'success' && locationsStatus == 'success' && (
        <NoteList
          canUpdate={canUpdate}
          showPestPacNote={showPestPacNote}
          showKnockStatusNote={showKnockStatusNote}
          count={count}
          entityId={leadId}
          entityType={ENTITY_TYPES.Lead}
          notes={notes}
          onChangePage={getLeadNotesByPage}
          onUpdate={onUpdateNoteLIst}
          page={page}
          take={take}
          externalLinkId={externalLinkId}
          sortBy={sortBy}
          detail={detail}
          backToNotes={backToNotes}
          setBackToNotes={setBackToNotes}
        />
      )}

      {status == 'fail' && (
        <Box width="fit-content">
          <Alert severity="error">Failed to load lead notes.</Alert>
        </Box>
      )}
    </Stack>
  )
}
